import { Bot, CheckCircle2, XCircle, HelpCircle, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAgentVerdict, useRecordHumanReview } from "@/hooks/useAgentVerdict";

interface AgentVerdictBadgeProps {
  companyId: string;
}

/**
 * Agent Verdict — displays the most recent web-search validation verdict for an
 * account, always rendered inline (no collapse) with the same typography as the
 * surrounding Targeting card content. Only renders when a verdict exists.
 *
 * Accept/Reject only stamps the human_verdict flag on rep_note_validations.
 * The rep still does the final classification (tier / qualification_reason) manually
 * in the existing AccountTargetingCard edit flow — this component is advisory only.
 */
export function AgentVerdictBadge({ companyId }: AgentVerdictBadgeProps) {
  const { data: verdict, isLoading } = useAgentVerdict(companyId);
  const recordReview = useRecordHumanReview();

  if (isLoading || !verdict) return null;

  const verdictConfig = {
    confirmed: {
      swatch: "bg-green-500",
      label: "Confirmed",
      icon: CheckCircle2,
      iconClass: "text-green-600",
    },
    refuted: {
      swatch: "bg-orange-500",
      label: "Refuted",
      icon: XCircle,
      iconClass: "text-orange-600",
    },
    inconclusive: {
      swatch: "bg-slate-400",
      label: "Inconclusive",
      icon: HelpCircle,
      iconClass: "text-slate-500",
    },
    error: {
      swatch: "bg-red-500",
      label: "Error",
      icon: XCircle,
      iconClass: "text-red-600",
    },
  } as const;

  const cfg = verdictConfig[verdict.verdict];
  const Icon = cfg.icon;

  const handleReview = async (humanVerdict: "accept" | "reject") => {
    try {
      await recordReview.mutateAsync({ validationId: verdict.id, humanVerdict });
      toast.success(
        humanVerdict === "accept" ? "Marked agent verdict as accepted" : "Rejected — your note stands",
        {
          description:
            humanVerdict === "accept"
              ? "Now finalize the tier/qualification reason below if you agree with the recommended action."
              : "No changes were made to this account.",
        }
      );
    } catch {
      toast.error("Failed to record review");
    }
  };

  return (
    <div className="rounded-md border bg-muted/30 px-4 py-3 space-y-3">
      {/* Header row */}
      <div className="flex flex-wrap items-center gap-2">
        <Bot className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Agent Verdict</span>
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${cfg.swatch}`} aria-hidden />
        <span className="inline-flex items-center gap-1 text-sm font-semibold">
          <Icon className={`h-4 w-4 ${cfg.iconClass}`} />
          {cfg.label}
        </span>
        {verdict.confidence !== null && (
          <span className="text-xs text-muted-foreground tabular-nums">
            {(verdict.confidence * 100).toFixed(0)}% confidence
          </span>
        )}
        {verdict.claim_category && (
          <Badge variant="outline" className="text-[10px] font-normal">
            {verdict.claim_category}
          </Badge>
        )}
        {verdict.reviewed_by_human && (
          <Badge variant="secondary" className="text-[10px]">
            {verdict.human_verdict === "accept" ? "✓ accepted by rep" : "✗ rejected by rep"}
          </Badge>
        )}
      </div>

      {/* Inline detail body — always visible */}
      <div className="space-y-3 text-sm">
        <div>
          <div className="text-xs font-semibold text-muted-foreground mb-1">Rep's note</div>
          <p className="whitespace-pre-wrap">{verdict.claim || "—"}</p>
        </div>

        {verdict.evidence && (
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">Agent evidence</div>
            <p className="whitespace-pre-wrap leading-relaxed text-sm">{verdict.evidence}</p>
          </div>
        )}

        {verdict.source_urls && verdict.source_urls.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">Sources</div>
            <ul className="space-y-1">
              {verdict.source_urls.slice(0, 5).map((url) => (
                <li key={url}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline break-all"
                  >
                    {url.replace(/^https?:\/\//, "").substring(0, 90)}
                    <ExternalLink className="h-3 w-3 shrink-0" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {verdict.recommended_action && (
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">Recommended action</div>
            <p className="text-sm italic">{verdict.recommended_action}</p>
          </div>
        )}
      </div>

      {/* Action buttons */}
      {!verdict.reviewed_by_human ? (
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            size="sm"
            variant="default"
            onClick={() => handleReview("accept")}
            disabled={recordReview.isPending}
          >
            Accept agent verdict
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleReview("reject")}
            disabled={recordReview.isPending}
          >
            Reject — my note stands
          </Button>
        </div>
      ) : verdict.human_reviewed_at ? (
        <p className="text-xs text-muted-foreground">
          Reviewed on {new Date(verdict.human_reviewed_at).toLocaleDateString()}.
          {verdict.human_verdict === "accept"
            ? " Update tier / qualification reason below if you want to apply the recommended action."
            : " Note kept as-is."}
        </p>
      ) : null}
    </div>
  );
}
